import {useAttachmentFilesQuery} from '@domain/file-download'
import {AttachmentFile} from '@hconnect/common/types'
import {useUpmPlantContext} from '@hconnect/upm'
import {InlineAttachmentsUpload} from '@widgets/common-attachments'
import React from 'react'

import {useAddMaterialOrderAttachments, useDeleteMaterialOrderAttachments} from '../../hooks'
import {useMaterialOrderPermissions} from '../../providers'
import type {MaterialOrder, MaterialOrderAttachment} from '../../types'

interface MaterialOrderAttachmentSectionProps {
  isDisabled: boolean
  materialOrder?: MaterialOrder
}

export const MaterialOrderAttachmentSection: React.FC<MaterialOrderAttachmentSectionProps> = ({
  materialOrder,
  isDisabled
}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {canAddDocuments, canDeleteDocuments} = useMaterialOrderPermissions()
  const {data: attachmentFiles, isFetching: areFilesFetching} = useAttachmentFilesQuery({
    attachments: materialOrder?.attachments ?? [],
    isPreview: true
  })
  const {mutateAsync: addMaterialOrderAttachments, isPending: isAddingAttachment} =
    useAddMaterialOrderAttachments()
  const {mutateAsync: deleteMaterialOrderAttachments, isPending: isDeletingAttachment} =
    useDeleteMaterialOrderAttachments()

  const handleAddAttachments = (attachemnts: AttachmentFile[]) => {
    if (!materialOrder || !attachemnts.length) {
      return
    }
    void addMaterialOrderAttachments({
      plantCode,
      materialOrderId: materialOrder.id,
      attachments: attachemnts
    })
  }

  const handleDeleteAttachments = (attachemnts: MaterialOrderAttachment[]) => {
    if (!materialOrder || !attachemnts.length) {
      return
    }
    void deleteMaterialOrderAttachments({
      plantCode,
      materialOrderId: materialOrder.id,
      attachmentIds: attachemnts.map(({id}) => id)
    })
  }

  const handleOrderAttachmentsChange = (attachments: AttachmentFile[]) => {
    const currentAttachmentIds =
      attachments.map(({id}) => id).filter((id) => id !== undefined) ?? []
    const attachmentsToAdd = attachments.filter((attachment) => attachment.id === undefined)
    const attachmentsToDelete =
      materialOrder?.attachments.filter(
        (attachment) => !currentAttachmentIds.includes(attachment.id)
      ) ?? []
    handleAddAttachments(attachmentsToAdd)
    handleDeleteAttachments(attachmentsToDelete)
  }

  const isLoading = isAddingAttachment || isDeletingAttachment
  const shouldDisableAttachmentUpload = isDisabled || isLoading
  return (
    <InlineAttachmentsUpload
      attachments={attachmentFiles ?? []}
      setAttachments={handleOrderAttachmentsChange}
      isAddingEnabled={canAddDocuments}
      isDeletingEnabled={canDeleteDocuments}
      isDisabled={shouldDisableAttachmentUpload}
      isLoading={areFilesFetching}
    />
  )
}
